/**
 * 🎥 **어느 화상 서비스를 쓸까**(관리자) — 고르고, 스펙을 보고, 방 만드는 법을 읽는 자리.
 *
 * ## 왜 고르게 하나 (2026-09-03 운영자 요청: 「웨일, 구글밋, jitsi 다 선택가능하게」)
 * 하나로 정해 두면 **그게 안 되는 사람은 길이 없다.** 실제로 그랬다 — 웨일온으로 정했더니
 * PC 참여자는 웨일 브라우저를 받아야 했고, 학교 PC 는 설치가 막혀 있기도 하다.
 *
 * ## 🔴 스펙을 **같이 보여 준다**
 * 「어느 게 좋아요?」의 답은 상황마다 다르다 — 사람이 몇이고, 얼마나 하고, 참여자가
 * 설치를 할 수 있나. 그 셋을 화면에 놓으면 운영자가 직접 고를 수 있다. 안 놓으면
 * **행사 당일에** 안다(60분에서 끊기거나, 25 접속을 넘겨 막히거나).
 *
 * ## 🔴 문구·판정은 여기서 안 짠다
 * 전부 `packages/shared` 의 `MEET_KINDS`·`meetSpecRows` 에서 온다. 관리자 화면도 같은 걸
 * 쓴다 — 화면마다 짜면 **아무 에러 없이 갈리고**, 운영자는 「여기선 된다는데 저기선
 * 안 된다」만 겪는다(이 저장소가 반나절을 쓴 사고의 모양).
 */
import { MEET_KINDS, meetKind, meetSpecRows, newJitsiUrl, type MeetKind } from "@greg-chick/shared";

interface Props {
  /** 지금 고른 갈래. */
  kind: MeetKind;
  onKind: (k: MeetKind) => void;
  /**
   * 🎲 우리가 방 주소를 만들어 줄 수 있는 갈래(Jitsi)에서 누르면 칸을 채운다.
   * 없으면 그 단추를 안 그린다 — **누를 수 있는데 아무 일도 안 나는 단추**를 만들지 않는다.
   */
  onFill?: (url: string) => void;
}

export function MeetPicker({ kind, onKind, onFill }: Props) {
  const info = meetKind(kind);
  return (
    <div className="meetpick">
      <div className="meetpick__tabs" role="radiogroup" aria-label="화상 서비스 고르기">
        {MEET_KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={k.id === kind}
            className={`meetpick__tab${k.id === kind ? " is-on" : ""}`}
            onClick={() => onKind(k.id)}
          >
            <span aria-hidden="true">{k.emoji}</span> {k.label}
          </button>
        ))}
      </div>

      {/* 🔴 좋은 점과 감수할 것을 **나란히** 둔다 — 좋은 점만 보이면 고를 수가 없다 */}
      <p className="meetpick__good">👍 {info.goodFor}</p>
      <p className="meetpick__watch">⚠️ {info.watchOut}</p>

      {/**
       * 📋 표는 `meetSpecRows` 가 문장까지 만든다. 화면은 늘어놓기만 한다 —
       * 여기서 「없음/확인 필요」를 판단하기 시작하면 관리자 화면과 갈린다.
       */}
      <dl className="meetpick__spec">
        {meetSpecRows(kind).map((r) => (
          <div key={r.label} className="meetpick__specrow">
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
      {info.spec.source ? (
        <p className="meetpick__src">
          {/* 🔴 숫자에는 **어디서 봤는지**를 붙인다 — 서비스 정책은 바뀐다 */}
          기준: {/^https?:\/\//.test(info.spec.source) ? (
            <a href={info.spec.source} target="_blank" rel="noopener noreferrer">
              {new URL(info.spec.source).host} ↗
            </a>
          ) : (
            info.spec.source
          )}{" "}
          ({info.spec.checkedAt} 확인)
        </p>
      ) : null}

      <div className="meetpick__acts">
        {info.makeUrl ? (
          <a className="btn" href={info.makeUrl} target="_blank" rel="noopener noreferrer">
            {info.emoji} 방 만들러 가기 ↗
          </a>
        ) : null}
        {/**
         * 🎲 **계정이 없어서 우리가 만들 수 있는 유일한 쪽**(Jitsi). 나머지는 로그인이
         * 있어야 방이 생겨서 자동화가 불가능하다 — 없는 단추를 그리지 않는다.
         */}
        {info.canGenerate && onFill ? (
          <button type="button" className="btn" onClick={() => onFill(newJitsiUrl())}>
            🎲 방 주소 만들기
          </button>
        ) : null}
      </div>

      {info.steps.length > 0 ? (
        <details className="meetpick__how">
          <summary>{info.label} 방은 어떻게 만드나요? (한 걸음씩)</summary>
          <ol className="meetpick__steps">
            {info.steps.map((s) => (
              <li key={s.title}>
                <b>{s.title}</b>
                <br />
                {s.body}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </div>
  );
}
